import {
  QueryFunctionContext,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';

import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, AutoRFQApi } from '@setvi/shared/enums';

import {
  RfqAnalyticsParams,
  RfqFilters,
  RfqItemsMetadataParams,
  RfqItemsResponse,
  RfqListAnalyticsParams
} from './Types';

export enum AutoRFQQueryKey {
  GET_ALL = 'GET_ALL_AUTO_RFQ',
  GET_ALL_SHARED = 'GET_ALL_SHARED_AUTO_RFQ',
  GET_SINGLE = 'GET_SINGLE_AUTO_RFQ',
  GET_ITEMS = 'GET_ITEMS_AUTO_RFQ',
  GET_MATCHED_ITEMS = 'GET_MATCHED_ITEMS_AUTO_RFQ',
  GENERATE_CSV = 'GENERATE_CSV_AUTO_RFQ',
  GET_LIST_ANALYTICS = 'GET_LIST_ANALYTICS_AUTO_RFQ',
  GET_LIST_ANALYTICS_SHARED = 'GET_LIST_ANALYTICS_SHARED_AUTO_RFQ',
  GET_RFQ_ITEM = 'GET_RFQ_ITEM_AUTO_RFQ',
  GET_RFQ_ANALYTICS = 'GET_RFQ_ANALYTICS_AUTO_RFQ',
  GET_RFQ_CONFIGURATIONS = 'GET_RFQ_CONFIGURATIONS_AUTO_RFQ',
  GET_ITEMS_METADATA = 'GET_RFQ_ITEMS_METADATA',
  GET_INTERNAL_NOTES = 'GET_RFQ_INTERNAL_NOTES'
}

export const getRfqsQuery = (params: RfqFilters, shared = false) => ({
  queryKey: [
    shared ? AutoRFQQueryKey.GET_ALL_SHARED : AutoRFQQueryKey.GET_ALL,
    params
  ],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      // The shared endpoint derives the caller from the JWT and ignores userId.
      endpoint: shared ? AutoRFQApi.GetAllShared : AutoRFQApi.GetAll,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      params: shared
        ? { ...params, userId: undefined, page: data.pageParam || params.page }
        : { ...params, page: data.pageParam || params.page }
    }),
  getNextPageParam: (lastPage: any, allPages: any) => {
    const allProducts = allPages.reduce(
      (acc: any, curr: any) => acc + curr?.data?.length,
      0
    );
    const currentPageNumber = allProducts / params.limit;

    return lastPage?.data?.length < params?.limit
      ? undefined
      : currentPageNumber + 1;
  }
});

export const getRfqQuery = (id: string | number) => ({
  queryKey: [AutoRFQQueryKey.GET_SINGLE, id],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetSingle.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getRfqItemQuery = (id: string | number) => ({
  queryKey: [AutoRFQQueryKey.GET_RFQ_ITEM, id],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetRfqItem.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});

export const getRfqAnalyticsQuery = (params: RfqAnalyticsParams) => ({
  queryKey: [AutoRFQQueryKey.GET_RFQ_ANALYTICS, params],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetRfqAnalytics.replace(
        ':id',
        params?.id.toString()
      ),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      params
    }),
  refetchOnWindowFocus: false,
  cacheTime: 0
});

export const getRfqItemsQuery = (
  id: string | number,
  params: RfqFilters
): UseInfiniteQueryOptions<RfqItemsResponse> => ({
  queryKey: [AutoRFQQueryKey.GET_ITEMS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: `${AutoRFQApi.GetItems.replace(':id', id?.toString())}`,
      method: AxiosMethods.GET,
      params: {
        ...params,
        page: data.pageParam || params.page
      },
      baseURL: process.env.REVOPS_API
    }),
  getNextPageParam: (lastPage: RfqItemsResponse) => {
    const total = lastPage?.total;
    const currPage = lastPage.page || 1;
    const currSize = lastPage.limit || 10;

    return currPage * currSize >= total ? undefined : currPage + 1;
  },
  refetchOnWindowFocus: false,
  cacheTime: 0
});

export const getRfqMatchedItemsQuery = (ids: string[]) => ({
  queryKey: [AutoRFQQueryKey.GET_MATCHED_ITEMS, ids],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetMatchedItems,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: ids
    })
});

export const generateRfqCSVQuery = (id: string | number) => ({
  queryKey: [AutoRFQQueryKey.GENERATE_CSV, id],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GenerateCSV.replace(':id', id.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      responseType: 'blob', // Treat response as a binary file
      headers: {
        Accept: 'text/csv' // Expect CSV response
      }
    })
});

export const getRfqListAnalyticsQuery = (
  params: RfqListAnalyticsParams,
  shared = false
) => ({
  queryKey: [
    shared
      ? AutoRFQQueryKey.GET_LIST_ANALYTICS_SHARED
      : AutoRFQQueryKey.GET_LIST_ANALYTICS,
    params
  ],
  queryFn: () =>
    axiosHelper({
      // The shared endpoint derives the caller from the JWT and ignores userId.
      endpoint: shared
        ? AutoRFQApi.GetListAnalyticsShared
        : AutoRFQApi.GetListAnalytics,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      params: shared ? { ...params, userId: undefined } : params
    })
});

export const getRfqItemsMetadataQuery = (
  rfqId: string,
  params: RfqItemsMetadataParams
) => ({
  queryKey: [AutoRFQQueryKey.GET_ITEMS_METADATA, rfqId, params],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetItemsMetadata.replace(':rfqId', rfqId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      params
    }),
  refetchOnWindowFocus: false
});

export const getRfqItemsPageQuery = (
  id: string | number,
  params: Omit<RfqFilters, 'page'>,
  page: number
) => ({
  queryKey: [AutoRFQQueryKey.GET_ITEMS, id, params, page],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.GetItems.replace(':id', id?.toString()),
      method: AxiosMethods.GET,
      params: { ...params, page },
      baseURL: process.env.REVOPS_API
    }),
  refetchOnWindowFocus: false,
  staleTime: 30_000
});

export interface InternalNote {
  _id: string;
  rfqId: string;
  rfqItemId: string;
  quoteId?: string;
  quoteItemId?: string;
  content: string;
  authorId: string;
  authorName: string;
  authorEmail: string;
  authorPhoto?: string;
  createdAt: string;
  updatedAt: string;
}

export const getInternalNotesQuery = (rfqItemId: string) => ({
  queryKey: [AutoRFQQueryKey.GET_INTERNAL_NOTES, rfqItemId],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.InternalNotes.replace(':rfqItemId', rfqItemId),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  select: (res: any): InternalNote[] => res?.Data || res?.data || res || []
});

export const getRfqConfigurationsQuery = () => ({
  queryKey: [AutoRFQQueryKey.GET_RFQ_CONFIGURATIONS],
  queryFn: () =>
    axiosHelper({
      endpoint: AutoRFQApi.Configurations,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    })
});
